/**
 * Grade levels dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LevelsDataTable } from '@/components/levels/LevelsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateLevelView } from '@/components/levels/CreateLevelView';
import { EditLevelView } from '@/components/levels/EditLevelView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link LevelsSection}. */
interface LevelsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: school levels list. */
export const LevelsSection: React.FC<LevelsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('levels');
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editLevelId, setEditLevelId] = React.useState<string | null>(null);

  const handleCreateLevel = () => {
    setCreateFormOpen(true);
    setEditLevelId(null);
  };

  const handleEditLevel = (id: string) => {
    setEditLevelId(id);
    setCreateFormOpen(false);
  };

  return (
    <>
      <LevelsDataTable
        layoutWidth={layoutWidth}
        onEditLevel={handleEditLevel}
        isSection={true}
        showCreateButton={canCreate}
        onCreateLevel={handleCreateLevel}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateLevelView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['levels'] });
            onSuccess?.();
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editLevelId !== null} onClose={() => setEditLevelId(null)}>
        {editLevelId && (
          <EditLevelView
            key={editLevelId}
            levelId={editLevelId}
            onClose={() => setEditLevelId(null)}
            onSuccess={() => {
              setEditLevelId(null);
              void queryClient.invalidateQueries({ queryKey: ['levels'] });
              onSuccess?.();
            }}
          />
        )}
      </SlidePanel>
    </>
  );
};
